import { memo, useCallback, useMemo } from 'react';

import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { NumberInput } from '@/modules/common/components/inputs/components/NumberInput';
import { StationaryArea } from '@thive/fddm-types';
import { getCommonNumber } from '@/modules/common/helpers/commonValue';
import {
  createEmptyAreas,
  pushAreaToItsCollection,
} from '@/modules/floorplanDesign/helpers/helpers';
import { DisabledReasonTooltip } from '../common/DisabledReasonTooltip';

interface Props {
  selectedStationaryAreas: StationaryArea[];
}

/** @description Parking and charging areas both have a priority, each on its own extension. */
const hasPriority = ({ stationaryType }: StationaryArea): boolean =>
  stationaryType === 'PARKING' || stationaryType === 'CHARGING';

const getPriority = (area: StationaryArea): number =>
  (area.stationaryType === 'CHARGING'
    ? area.chargingExtension?.priority
    : area.parkingExtension?.priority) ?? 0;

/** @description Returns the area with the priority set on the extension that matches its type. */
const withPriority = (area: StationaryArea, priority: number): StationaryArea =>
  area.stationaryType === 'CHARGING'
    ? { ...area, chargingExtension: { ...area.chargingExtension, priority } }
    : { ...area, parkingExtension: { ...area.parkingExtension, priority } };

/** @description Insertion and extraction areas both have a wait time, each on its own extension. */
const hasWaitDuration = ({ stationaryType }: StationaryArea): boolean =>
  stationaryType === 'INSERTION' || stationaryType === 'EXTRACTION';

const getWaitDuration = (area: StationaryArea): number =>
  (area.stationaryType === 'EXTRACTION'
    ? area.extractionExtension?.waitDurationInSimulation
    : area.insertionExtension?.waitDurationInSimulation) ?? 0;

/** @description Returns the area with the wait time set on the extension that matches its type. */
const withWaitDuration = (area: StationaryArea, waitDurationInSimulation: number): StationaryArea =>
  area.stationaryType === 'EXTRACTION'
    ? {
        ...area,
        extractionExtension: { ...area.extractionExtension, waitDurationInSimulation },
      }
    : {
        ...area,
        insertionExtension: { ...area.insertionExtension, waitDurationInSimulation },
      };

function ExtensionsComponent({ selectedStationaryAreas }: Props) {
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  // One priority field for parking and charging areas together
  const priorityAreas = useMemo(
    () => selectedStationaryAreas.filter(hasPriority),
    [selectedStationaryAreas],
  );
  // Only editable when every selected area has a priority (no insertion/extraction mixed in)
  const isPriorityPartialSelection = priorityAreas.length !== selectedStationaryAreas.length;

  // null shows 'Mixed'
  const priority = useMemo(() => getCommonNumber(priorityAreas.map(getPriority)), [priorityAreas]);

  // One wait time field for insertion and extraction areas together
  const waitDurationAreas = useMemo(
    () => selectedStationaryAreas.filter(hasWaitDuration),
    [selectedStationaryAreas],
  );
  // Only editable when every selected area has a wait time (no parking/charging mixed in)
  const isWaitDurationPartialSelection =
    waitDurationAreas.length !== selectedStationaryAreas.length;

  // null shows 'Mixed'
  const waitDuration = useMemo(
    () => getCommonNumber(waitDurationAreas.map(getWaitDuration)),
    [waitDurationAreas],
  );

  const onPriorityChange = useCallback(
    (newValue: number) => {
      if (isPriorityPartialSelection) return;

      const updatedAreas = createEmptyAreas();
      priorityAreas.forEach((area) => {
        pushAreaToItsCollection(updatedAreas, withPriority(area, newValue));
      });
      fddmSvc.updateAreas(updatedAreas);
    },
    [fddmSvc, isPriorityPartialSelection, priorityAreas],
  );

  const onWaitDurationChange = useCallback(
    (newValue: number) => {
      if (isWaitDurationPartialSelection) return;

      const updatedAreas = createEmptyAreas();
      waitDurationAreas.forEach((area) => {
        pushAreaToItsCollection(updatedAreas, withWaitDuration(area, newValue));
      });
      fddmSvc.updateAreas(updatedAreas);
    },
    [fddmSvc, isWaitDurationPartialSelection, waitDurationAreas],
  );

  return (
    <>
      {priorityAreas.length > 0 && (
        <PropertiesLabel i18nkey='interface:properties.area.priority'>
          <DisabledReasonTooltip
            reasonKey={
              isPriorityPartialSelection
                ? 'interface:properties.area.partial_selection.only_parking_or_charging'
                : undefined
            }
          >
            <NumberInput
              // undefined (not false) keeps NumberInput's own read-only-mode check
              disabled={isPriorityPartialSelection ? true : undefined}
              value={priority}
              sx={{ width: '100%' }}
              minValue={0}
              onChange={onPriorityChange}
            />
          </DisabledReasonTooltip>
        </PropertiesLabel>
      )}
      {waitDurationAreas.length > 0 && (
        <PropertiesLabel i18nkey='interface:properties.area.block_duration'>
          <DisabledReasonTooltip
            reasonKey={
              isWaitDurationPartialSelection
                ? 'interface:properties.area.partial_selection.only_insertion_or_extraction'
                : undefined
            }
          >
            <NumberInput
              disabled={isWaitDurationPartialSelection ? true : undefined}
              value={waitDuration}
              unit='sec'
              sx={{ width: '100%' }}
              minValue={0}
              onChange={onWaitDurationChange}
            />
          </DisabledReasonTooltip>
        </PropertiesLabel>
      )}
    </>
  );
}

export const Extensions = memo(ExtensionsComponent);
